"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { createRoot, type Root } from "react-dom/client";
import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from "embla-carousel-react";

type EmblaApi = NonNullable<UseEmblaCarouselType[1]>;
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";

import { useTranslator } from "@/lib/translation-context";

/**
 * Progressive enhancement for `<div data-tc-carousel>` wrappers written by
 * the operator-web BlockNote image-carousel block.
 *
 * Server-side: the tour body HTML is sanitised and dropped in via
 * `dangerouslySetInnerHTML`, so every figure inside the wrapper is real DOM
 * — readable + indexable + accessible even with JS disabled.
 *
 * Client-side: on mount we find each wrapper, extract slide data from its
 * existing children, replace the children with an Embla-driven slideshow,
 * and unmount cleanly on teardown.
 */

interface SlideData {
  src: string;
  alt: string;
  caption: string;
}

interface CarouselProps {
  slides: SlideData[];
  aspect: string;
  autoplay: boolean;
}

function CarouselPanel({
  slides,
  aspect,
  autoplay,
}: CarouselProps): React.ReactElement {
  const t = useTranslator();
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: slides.length > 1,
  });
  const [selectedIdx, setSelectedIdx] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = (api: EmblaApi): void => {
      setSelectedIdx(api.selectedScrollSnap());
    };
    emblaApi.on("select", onSelect);
    onSelect(emblaApi);
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi || !autoplay || slides.length < 2) return;
    const interval = window.setInterval(() => {
      emblaApi.scrollNext();
    }, 4500);
    return () => window.clearInterval(interval);
  }, [emblaApi, autoplay, slides.length]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback(
    (idx: number) => emblaApi?.scrollTo(idx),
    [emblaApi],
  );

  return (
    <div
      className="tc-carousel-mounted"
      style={
        {
          "--tc-carousel-aspect": aspect.replace("/", " / "),
        } as React.CSSProperties
      }
    >
      <div className="tc-carousel-viewport" ref={emblaRef}>
        <div className="tc-carousel-container">
          {slides.map((slide, idx) => (
            <figure key={`${slide.src}-${idx}`} className="tc-carousel-slide">
              {/* Plain <img> is intentional: the source URL is already a presigned
                  R2 link with the correct variant; routing it through next/image
                  inside dangerouslySetInnerHTML-derived state is more cost than
                  win for a secondary visual. */}
              <img
                src={slide.src}
                alt={slide.alt}
                loading="lazy"
                decoding="async"
              />
              {slide.caption ? (
                <figcaption className="tc-carousel-caption">
                  {slide.caption}
                </figcaption>
              ) : null}
            </figure>
          ))}
        </div>

        {slides.length > 1 ? (
          <>
            <button
              type="button"
              className="tc-carousel-nav tc-carousel-nav-prev"
              onClick={scrollPrev}
              aria-label={t("Previous Slide")}
            >
              <ChevronLeftIcon size={18} strokeWidth={2} />
            </button>
            <button
              type="button"
              className="tc-carousel-nav tc-carousel-nav-next"
              onClick={scrollNext}
              aria-label={t("Next Slide")}
            >
              <ChevronRightIcon size={18} strokeWidth={2} />
            </button>
          </>
        ) : null}
      </div>

      {slides.length > 1 ? (
        // A plain labelled group, NOT a tablist. The previous `role="tablist"`
        // / `role="tab"` promised a widget this does not implement: no
        // `aria-controls`, no `role="tabpanel"` on the slides, no roving
        // tabindex, no arrow-key handling. A screen reader announced tabs that
        // controlled nothing. `aria-current` states which slide is showing
        // without claiming a pattern we don't honour.
        <div
          className="tc-carousel-dots"
          role="group"
          aria-label={t("Carousel Slides")}
        >
          {slides.map((_, idx) => (
            <button
              key={idx}
              type="button"
              aria-current={selectedIdx === idx || undefined}
              aria-label={t("Go to Slide {n}", { n: idx + 1 })}
              className="tc-carousel-dot"
              data-active={selectedIdx === idx}
              onClick={() => scrollTo(idx)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function extractSlides(wrapper: HTMLElement): SlideData[] {
  // Primary form: JSON in `data-tc-images` (what the editor writes).
  const json = wrapper.getAttribute("data-tc-images");
  if (json) {
    try {
      const parsed = JSON.parse(json) as unknown;
      if (Array.isArray(parsed)) {
        const slides = parsed
          .filter(
            (it): it is { url?: unknown; alt?: unknown; caption?: unknown } =>
              typeof it === "object" && it !== null,
          )
          .map((it) => ({
            src: typeof it.url === "string" ? it.url : "",
            alt: typeof it.alt === "string" ? it.alt : "",
            caption: typeof it.caption === "string" ? it.caption : "",
          }))
          .filter((s) => s.src.length > 0);
        if (slides.length > 0) return slides;
      }
    } catch {
      // fall through to figure-extraction below
    }
  }

  // Legacy form: child <figure> elements (kept so SSR-expanded HTML still
  // upgrades correctly).
  const figures = Array.from(
    wrapper.querySelectorAll<HTMLElement>(":scope > figure"),
  );
  return figures
    .map((fig) => {
      const img = fig.querySelector("img");
      const caption = fig.querySelector("figcaption");
      return {
        // `data-defer-src` first: optimizeBodyImages defers body images by
        // moving their src into that attribute, and this extraction can run
        // BEFORE LazyEmbeds restores it — reading only `src` would yield ""
        // and the filter below would silently drop every legacy slide.
        // Checking both makes the two mechanisms order-independent.
        src:
          img?.getAttribute("data-defer-src") ?? img?.getAttribute("src") ?? "",
        alt: img?.getAttribute("alt") ?? "",
        caption: caption?.textContent?.trim() ?? "",
      };
    })
    .filter((s) => s.src.length > 0);
}

export function CarouselUpgrader(): null {
  const mountedRef = useRef<Map<HTMLElement, Root>>(new Map());

  useEffect(() => {
    const roots = mountedRef.current;
    // Wrappers whose slide JSON parsed to nothing. They cannot gain slides
    // without an attribute change and attributes are not observed, so
    // re-examining them on every mutation is pure cost — see the observer.
    const barren = new WeakSet<HTMLElement>();
    // Exactly the children this effect hid, so teardown unhides those and not
    // whatever else happens to be `hidden` in the wrapper by then.
    const hiddenOriginals = new Map<HTMLElement, HTMLElement[]>();

    const upgradeAll = (): void => {
      // Drop roots whose wrapper has left the document. Nothing else prunes
      // this map, and if the component is reconciled in place across a
      // tour-to-tour client navigation the effect never re-runs — so the old
      // roots would pin detached DOM alive and their autoplay intervals would
      // keep firing `scrollNext` on carousels nobody can see.
      roots.forEach((root, wrapper) => {
        if (!wrapper.isConnected) {
          root.unmount();
          roots.delete(wrapper);
          hiddenOriginals.delete(wrapper);
        }
      });

      const wrappers = document.querySelectorAll<HTMLElement>(
        "[data-tc-carousel]:not([data-tc-upgraded])",
      );
      wrappers.forEach((wrapper) => {
        if (barren.has(wrapper)) return;
        const slides = extractSlides(wrapper);
        if (slides.length === 0) {
          // An empty carousel block is a normal operator state — the block
          // exists before any image is picked. Only park it when the JSON is
          // present and genuinely empty; the legacy <figure> form has its
          // slides appended later, so it must stay eligible.
          if (wrapper.hasAttribute("data-tc-images")) barren.add(wrapper);
          return;
        }

        const aspect = wrapper.dataset.aspect ?? "16/9";
        const autoplay = wrapper.dataset.autoplay === "true";

        wrapper.setAttribute("data-tc-upgraded", "");

        // Hide the server-rendered children rather than deleting them, and
        // mount React into a container appended ALONGSIDE them.
        //
        // `wrapper.innerHTML = ""` made the upgrade one-way and destructive.
        // React owned the wrapper, so unmounting emptied it: any teardown
        // where the wrapper outlives this component left a blank div with no
        // observer and no static content to rebuild from — and the file's own
        // promise of markup that survives with JS disabled was void from the
        // first upgrade onward. It also raced the streaming parser: for the
        // legacy <figure> form the observer can fire while figures are still
        // arriving, and clearing then would have thrown away the ones still in
        // flight. Hiding is reversible and keeps the SSR content as the real
        // fallback.
        const originals = ([...wrapper.children] as HTMLElement[]).filter(
          (child) => !child.hidden,
        );
        for (const child of originals) {
          child.hidden = true;
          // Keeping the fallback means both copies of each slide are in the
          // DOM. The React copy is the one anyone sees, so make sure the
          // hidden originals can never cost a request: `hidden` alone does not
          // stop an <img> being fetched, and LazyEmbeds restores deferred srcs
          // by walking the document, which would reach these too.
          for (const img of child.querySelectorAll("img")) {
            img.loading = "lazy";
          }
        }

        const host = document.createElement("div");
        host.setAttribute("data-tc-carousel-root", "");
        wrapper.append(host);

        const root = createRoot(host);
        hiddenOriginals.set(wrapper, originals);
        root.render(
          <CarouselPanel slides={slides} aspect={aspect} autoplay={autoplay} />,
        );
        roots.set(wrapper, root);
      });
    };

    upgradeAll();

    /**
     * Keep watching. This component sits OUTSIDE the Suspense boundary the
     * itinerary resolves inside, so on a streamed page the mount-time scan can
     * run before a single itinerary carousel exists. Scanning once left those
     * wrappers in the finished page forever — present, empty, never upgraded —
     * while a carousel in the main description worked purely because it
     * happened to already be there. That is what made the bug look
     * intermittent.
     *
     * Re-running is cheap and idempotent: the selector skips anything already
     * upgraded, so our own mutations (the attribute, the appended host, React's
     * render) settle immediately instead of looping.
     */
    // Coalesced to one scan per frame. The callback fires for every childList
    // mutation anywhere under <body> — the booking sidebar, the availability
    // calendar, LazyEmbeds — and a document-wide `querySelectorAll` on each of
    // those is a standing INP cost on the busiest page in the app. A frame is
    // also the right granularity for streamed markup, which arrives in bursts.
    let scheduled = 0;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = requestAnimationFrame(() => {
        scheduled = 0;
        upgradeAll();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      if (scheduled) cancelAnimationFrame(scheduled);
      roots.forEach((root, wrapper) => {
        // Defer unmount to the next microtask — React forbids unmount during
        // the same commit that scheduled it.
        queueMicrotask(() => {
          root.unmount();
          // Reverse the upgrade rather than leaving a hole. React only ever
          // owned the appended host, so removing it and unhiding the originals
          // restores the server-rendered figures exactly as they arrived —
          // which is what makes a re-upgrade (StrictMode's double-invoke, or a
          // later remount) land on real content instead of an empty div.
          //
          // Removing the host is also what TRIGGERS that re-upgrade: it is a
          // childList mutation the next effect's observer sees. The scan alone
          // cannot do it, because `data-tc-upgraded` is still set at that
          // point and the observer watches childList only. Do not "optimise"
          // this into reusing the host — the re-upgrade would stop silently.
          wrapper.querySelector(":scope > [data-tc-carousel-root]")?.remove();
          // Only what we hid — a child that was already `hidden` when we found
          // it (author markup, another enhancer) stays that way.
          for (const child of hiddenOriginals.get(wrapper) ?? []) {
            child.hidden = false;
          }
          hiddenOriginals.delete(wrapper);
          wrapper.removeAttribute("data-tc-upgraded");
        });
      });
      roots.clear();
    };
  }, []);

  return null;
}
